const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? "" : "s"} ago`;
}

/**
 * How long ago `iso` was, in words — "3 days ago", "just now".
 *
 * The versions sheet leads with this rather than a date (FR-DOC-4): "3 days
 * ago" says whether a page is stale at a glance, and the exact timestamp is
 * still one hover away via `formatAbsolute` on the same row.
 *
 * `now` defaults to the wall clock rather than the fixture's own frozen
 * `FIXTURE_NOW` (`lib/reportClock.ts`) — a real revision from a real author
 * will only ever have a real `Date.now()` to be relative to.
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;

  const diff = Math.max(0, now - then);
  if (diff < MINUTE) return "just now";
  if (diff < HOUR) return plural(Math.round(diff / MINUTE), "minute");
  if (diff < DAY) return plural(Math.round(diff / HOUR), "hour");
  if (diff < MONTH) return plural(Math.round(diff / DAY), "day");
  if (diff < YEAR) return plural(Math.round(diff / MONTH), "month");
  return plural(Math.round(diff / YEAR), "year");
}

const ABSOLUTE_FORMAT: Intl.DateTimeFormatOptions = {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/** The full date and time behind a `relativeTime` — the hover title. */
export function formatAbsolute(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  return new Intl.DateTimeFormat("en-IN", ABSOLUTE_FORMAT).format(parsed);
}

const BANNER_DAY: Intl.DateTimeFormatOptions = {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
};
const BANNER_TIME: Intl.DateTimeFormatOptions = {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/**
 * "12 Sep, 14:03" — the "viewing an old version" banner's own date format.
 *
 * No year, unlike `formatAbsolute`'s tooltip: the banner is read next to the
 * revision that produced it, minutes after it was clicked, so the year is
 * the one thing about it nobody needs restated.
 */
export function formatBanner(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const day = new Intl.DateTimeFormat("en-IN", BANNER_DAY).format(parsed);
  const time = new Intl.DateTimeFormat("en-IN", BANNER_TIME).format(parsed);
  return `${day}, ${time}`;
}
